import { MessageSquare } from "lucide-react";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ChannelList, { type ChannelRowDnd } from './ChannelList';
import { ChannelRow } from './ChannelRow';
import { ChannelListSkeleton } from '../ui/skeletons';
import FitText from '../ui/FitText';
import type { Channel, ChannelFolder } from '../../types/channel';
import type { Role } from '../../types/role';
import { api } from '../../lib/api';
import { toast, askPrompt } from '../../lib/ui';

type ChannelSidebarProps = {
  width: number;
  spaceName?: string;
  isSpaceOwner: boolean;
  isDmSpace: boolean;
  currentVoidId: string;
  currentChannelId: string;
  channels: Channel[];
  folders: ChannelFolder[];
  channelOrder: string[];
  unread: Record<string, number>;
  voicePresence?: Record<string, string[]>;
  members?: { id: string; name?: string; username?: string }[];
  homeChannelId?: string | null;
  onSelectChannel: (channelId: string) => void;
  onQuickAddChannel: () => void;
  onCreateFolder: () => void;
  onAddChannelToFolder: (folderId: string) => void;
  onToggleFavorite: (channelId: string) => void;
  isFavorite: (channelId: string) => boolean;
  onMoveChannel: (channelId: string, folderId: string | null) => void;
  onRenameChannel: (channel: Channel) => void;
  onDeleteChannel: (channel: Channel) => void;
  onChannelReorder: (channelId: string, targetFolderId: string | null, beforeChannelId: string | null) => void;
  onFolderReorder: (folderId: string, beforeFolderId: string | null) => void;
  roles: Role[];
  onUpdateChannelAccess: (channelId: string, roleIds: string[]) => Promise<void> | void;
  onToggleChannelLock: (channel: Channel) => Promise<void> | void;
  onSetHomeChannel?: (channelId: string) => void;
  footer?: React.ReactNode;
  /** Channels may still be arriving: show skeleton rows instead of "No channels yet". */
  loading?: boolean;
  token?: string;
};

function ChannelSidebar({
  width,
  spaceName,
  isSpaceOwner,
  isDmSpace,
  currentVoidId,
  currentChannelId,
  channels,
  folders,
  channelOrder,
  unread,
  voicePresence,
  members,
  homeChannelId,
  onSelectChannel,
  onQuickAddChannel,
  onCreateFolder,
  onAddChannelToFolder,
  onToggleFavorite,
  isFavorite,
  onMoveChannel,
  onRenameChannel,
  onDeleteChannel,
  onChannelReorder,
  onFolderReorder,
  roles,
  onUpdateChannelAccess,
  onToggleChannelLock,
  onSetHomeChannel,
  footer,
  token,
  loading = false,
}: ChannelSidebarProps) {
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [notifMode, setNotifMode] = useState<'group' | 'dm_style'>('group');
  const [notifSaving, setNotifSaving] = useState(false);

  // Fetch the notification override whenever the settings modal opens for a channel
  useEffect(() => {
    if (!menuFor || !token) return;
    api.getAuth(`/channels/notification-override?channelId=${encodeURIComponent(menuFor)}`, token)
      .then((res: any) => setNotifMode(res?.mode === 'dm_style' ? 'dm_style' : 'group'))
      .catch(() => setNotifMode('group'));
  }, [menuFor, token]);

  const saveNotifMode = async (mode: 'group' | 'dm_style') => {
    if (!menuFor || !token) return;
    setNotifSaving(true);
    setNotifMode(mode);
    try {
      await api.postAuth('/channels/notification-override', { channelId: menuFor, mode }, token);
      toast('Notification preference saved', 'success');
    } catch { toast('Failed to save preference', 'error'); } finally {
      setNotifSaving(false);
    }
  };
  const listScrollRef = useRef<HTMLDivElement | null>(null);
  const [accessEditFor, setAccessEditFor] = useState<string | null>(null);
  const [accessSelection, setAccessSelection] = useState<string[]>([]);
  const activeChannel = useMemo(() => (menuFor ? channels.find((c) => c.id === menuFor) : null), [menuFor, channels]);
  const homeShortId = useMemo(() => {
    if (!homeChannelId) return null;
    return homeChannelId.includes(':') ? homeChannelId.split(':').slice(-1)[0] : homeChannelId;
  }, [homeChannelId]);
  const memberNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of (members || [])) {
      const label = (m?.name || m?.username || '').trim();
      if (label) map.set(m.id, label);
    }
    return map;
  }, [members]);

  const ordered = useMemo(() => {
    const order = channelOrder || [];
    return [...channels].sort((a, b) => {
      const ia = order.indexOf(a.id); const ib = order.indexOf(b.id);
      if (ia !== -1 || ib !== -1) return (ia === -1 ? 1e9 : ia) - (ib === -1 ? 1e9 : ib);
      const posDiff = Number(b.position || 0) - Number(a.position || 0);
      if (posDiff !== 0) return posDiff;
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [channels, channelOrder]);

  // Sliding active-channel indicator: a 3px bar positioned against the measured
  // active row, so selection visibly travels between items instead of teleporting.
  const listWrapRef = useRef<HTMLDivElement | null>(null);
  const indicatorRef = useRef<HTMLSpanElement | null>(null);
  const indicatorSettled = useRef(false);

  const positionIndicator = useCallback(() => {
    const wrap = listWrapRef.current;
    const bar = indicatorRef.current;
    if (!wrap || !bar) return;
    const active = wrap.querySelector<HTMLElement>('[data-active-channel]');
    if (!active) {
      bar.style.opacity = '0';
      indicatorSettled.current = false;
      return;
    }
    const top = active.getBoundingClientRect().top - wrap.getBoundingClientRect().top;
    // First placement after mount/space switch lands instantly; only later moves slide
    if (!indicatorSettled.current) bar.style.transition = 'none';
    bar.style.opacity = '1';
    bar.style.transform = `translateY(${top}px)`;
    bar.style.height = `${active.offsetHeight}px`;
    if (!indicatorSettled.current) {
      requestAnimationFrame(() => { bar.style.transition = ''; });
      indicatorSettled.current = true;
    }
  }, []);

  useLayoutEffect(positionIndicator);

  // Folder collapse/expand resizes the list without re-rendering this component
  useEffect(() => {
    const wrap = listWrapRef.current;
    if (!wrap || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(positionIndicator);
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [positionIndicator, ordered.length]);

  const renderChannel = (ch: Channel, dnd: ChannelRowDnd) => {
    const count = unread[`${currentVoidId}:${ch.id}`] || unread[ch.id] || 0;
    const menuOpen = menuFor === ch.id;
    return (
      <ChannelRow
        channel={ch}
        active={ch.id === currentChannelId}
        unread={count > 0}
        dnd={dnd}
        onSelect={() => { setMenuFor(null); onSelectChannel(ch.id); }}
        voiceUsers={voicePresence?.[ch.id]}
        voiceNameById={memberNameById}
        actions={(
          <div className="absolute top-1 right-1 flex items-center gap-1 opacity-0 group-hover:opacity-100">
            <button
              className="p-1 rounded hover:bg-neutral-800 text-xs"
              title="Channel options"
              onClick={(e) => { e.stopPropagation(); setMenuFor(menuOpen ? null : ch.id); }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9c.26.49.4 1.04.4 1.6a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />
              </svg>
            </button>
          </div>
        )}
      />
    );
  };

  return (
    <aside aria-label="Channels" className="hidden md:flex bg-panel-2 flex-col min-h-0" style={{ width: `clamp(10rem, 24vw, ${width}px)` }}>
      <div className="h-12 flex items-center px-3 bg-panel-0" title={spaceName || currentVoidId}>
        <FitText className="flex-1 font-medium text-fg">{spaceName || currentVoidId}</FitText>
      </div>
      <div className="p-2 text-fg-2 text-sm bg-panel-0 flex items-center justify-between">
        <span>Channels</span>
        <div className="flex items-center gap-2">
          {isSpaceOwner && (
            <>
              <button
                type="button"
                className="h-7 w-7 rounded-md text-emerald-200 hover:text-white flex items-center justify-center"
                onClick={onQuickAddChannel}
                aria-label="Quick add channel"
                title="Quick add channel"
              >
                +
              </button>
              <button
                type="button"
                className="h-7 w-7 rounded-md text-emerald-200 hover:text-white flex items-center justify-center border border-emerald-700/50"
                onClick={onCreateFolder}
                aria-label="Add folder"
                title="Add folder"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
                  <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />
                  <path d="M12 10v6" />
                  <path d="M9 13h6" />
                </svg>
              </button>
            </>
          )}
        </div>
      </div>
      <div ref={listScrollRef} className="flex-1 overflow-auto">
        {ordered.length === 0 && loading ? (
          <ChannelListSkeleton />
        ) : ordered.length === 0 ? (
          <div className="p-4 space-y-2 text-center">
            <MessageSquare className="mx-auto h-6 w-6 text-fg-3" strokeWidth={1.5} />
            <p className="text-xs font-medium text-fg-2">No channels yet</p>
            <p className="text-meta text-fg-3 leading-relaxed">
              Channels are rooms inside a Space — create one for each topic or group.
            </p>
            {isSpaceOwner && (
              <button
                onClick={onQuickAddChannel}
                className="mt-1 w-full rounded-lg border border-neutral-700 bg-neutral-800/50 py-1.5 text-xs text-fg-2 hover:border-emerald-700/60 hover:text-emerald-300 transition-colors"
              >
                + Create first channel
              </button>
            )}
          </div>
        ) : (
          <div ref={listWrapRef} className="relative p-1 space-y-0">
            <span ref={indicatorRef} aria-hidden className="channel-active-indicator" />
            <ChannelList
              channels={ordered}
              folders={folders}
              scrollRef={listScrollRef}
              renderChannel={renderChannel}
              canManageFolders={isSpaceOwner && !isDmSpace}
              onAddChannelToFolder={(folderId) => onAddChannelToFolder(folderId)}
              onChannelReorder={(channelId, folderId, beforeId) => onChannelReorder(channelId, folderId, beforeId)}
              onFolderReorder={onFolderReorder}
            />
          </div>
        )}
      </div>
      {footer ? (
        <div className="border-t border-neutral-800 bg-panel-2/95 backdrop-blur px-2 py-2">
          {footer}
        </div>
      ) : null}
      {menuFor && activeChannel && (
        <div
          className="fixed inset-0 z-[6500] flex items-center justify-center bg-black/60 p-4"
          onClick={() => { setMenuFor(null); setAccessEditFor(null); }}
        >
          <div
            className="w-full max-w-md rounded-xl border border-neutral-800 bg-neutral-950 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 border-b border-neutral-800 p-4">
              <div className="min-w-0">
                <div className="text-xs text-fg-3">Channel Settings</div>
                <div className="text-lg font-medium text-fg truncate">{activeChannel.name}</div>
              </div>
              <button
                className="h-8 w-8 rounded-full border border-neutral-800 text-fg-2 hover:text-fg"
                onClick={() => { setMenuFor(null); setAccessEditFor(null); }}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="p-4 space-y-2">
              <button
                className="w-full text-left px-3 py-2 rounded-md border border-neutral-800 text-fg hover:bg-neutral-900"
                onClick={() => { onToggleFavorite(activeChannel.id); setMenuFor(null); }}
              >
                {isFavorite(activeChannel.id) ? 'Unfavorite' : 'Favorite'}
              </button>
              {/* Notification mode */}
              <div className="rounded-md border border-neutral-800 p-3 space-y-2">
                <div className="text-xs text-fg-3">Notifications</div>
                {(['group', 'dm_style'] as const).map(mode => (
                  <button
                    key={mode}
                    disabled={notifSaving}
                    onClick={() => saveNotifMode(mode)}
                    className={`w-full flex items-start gap-3 rounded-md px-3 py-2 text-left transition-colors ${notifMode === mode ? 'bg-emerald-500/15 border border-emerald-500/40' : 'border border-neutral-800 hover:bg-neutral-900'} disabled:opacity-50`}
                  >
                    <span className={`mt-0.5 h-4 w-4 shrink-0 rounded-full border-2 flex items-center justify-center ${notifMode === mode ? 'border-emerald-400' : 'border-neutral-600'}`}>
                      {notifMode === mode && <span className="h-2 w-2 rounded-full bg-emerald-400" />}
                    </span>
                    <span>
                      <span className="block text-sm font-medium text-fg">
                        {mode === 'group' ? 'Group Notifications' : 'DM Style Notifications'}
                      </span>
                      <span className="block text-xs text-fg-3 mt-0.5">
                        {mode === 'group'
                          ? 'One notification per unread session — notified again after you check the channel.'
                          : 'Notify on every message, just like a direct message.'}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
              {isSpaceOwner && !isDmSpace && (
                <>
                  <button
                    className="w-full text-left px-3 py-2 rounded-md border border-neutral-800 text-fg hover:bg-neutral-900 disabled:opacity-50"
                    onClick={() => { onSetHomeChannel?.(activeChannel.id); setMenuFor(null); }}
                    disabled={!onSetHomeChannel || homeShortId === activeChannel.id}
                  >
                    {homeShortId === activeChannel.id ? 'Default channel' : 'Set as default'}
                  </button>
                  <button
                    className="w-full text-left px-3 py-2 rounded-md border border-neutral-800 text-fg hover:bg-neutral-900"
                    onClick={() => { onRenameChannel(activeChannel); setMenuFor(null); }}
                  >
                    Rename
                  </button>
                  <button
                    className="w-full text-left px-3 py-2 rounded-md border border-neutral-800 text-fg hover:bg-neutral-900"
                    onClick={async () => { const fid = ((await askPrompt({ title: 'Move to folder', label: 'Folder id (leave empty for root)', initialValue: activeChannel.folderId || '' })) || '').trim(); onMoveChannel(activeChannel.id, fid || null); setMenuFor(null); }}
                  >
                    Move to folder
                  </button>
                  <button
                    className="w-full text-left px-3 py-2 rounded-md border border-neutral-800 text-fg hover:bg-neutral-900"
                    onClick={() => { setAccessEditFor(activeChannel.id); setAccessSelection((activeChannel as any).allowedRoles || []); }}
                  >
                    Permissions
                  </button>
                  <button
                    className="w-full text-left px-3 py-2 rounded-md border border-neutral-800 text-fg hover:bg-neutral-900"
                    onClick={async () => { await onToggleChannelLock(activeChannel); setMenuFor(null); }}
                  >
                    {activeChannel.locked ? 'Unlock' : 'Lock'}
                  </button>
                  <button
                    className="w-full text-left px-3 py-2 rounded-md border border-red-900/40 text-red-300 hover:bg-red-900/30"
                    onClick={() => { onDeleteChannel(activeChannel); setMenuFor(null); }}
                  >
                    Delete
                  </button>
                </>
              )}
            </div>
            {accessEditFor === activeChannel.id && (
              <div className="border-t border-neutral-800 p-4 space-y-2">
                <div className="text-sm font-medium text-fg">Channel access</div>
                <div className="text-xs text-fg-3">Select roles allowed in this channel. Empty = everyone.</div>
                <div className="max-h-48 overflow-auto space-y-1">
                  {roles.map((r) => (
                    <label key={r.id} className="flex items-center gap-2 text-sm text-fg">
                      <input
                        type="checkbox"
                        className="accent-emerald-500"
                        checked={accessSelection.includes(r.id)}
                        onChange={() => {
                          setAccessSelection((prev) => prev.includes(r.id) ? prev.filter((x) => x !== r.id) : [...prev, r.id]);
                        }}
                      />
                      <span className="truncate" style={r.color ? { color: r.color } : undefined}>{r.name}</span>
                    </label>
                  ))}
                  {roles.length === 0 && <div className="text-xs text-fg-3">No roles defined.</div>}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    className="px-3 py-1.5 rounded border border-neutral-700 text-fg hover:bg-neutral-800/60 text-sm"
                    onClick={() => setAccessSelection([])}
                  >
                    Allow everyone
                  </button>
                  <div className="ml-auto flex items-center gap-2">
                    <button
                      className="px-3 py-1.5 rounded border border-neutral-700 text-fg hover:bg-neutral-800/60 text-sm"
                      onClick={() => { setAccessEditFor(null); setMenuFor(null); }}
                    >
                      Cancel
                    </button>
                    <button
                      className="px-3 py-1.5 rounded border border-emerald-600 text-emerald-200 hover:bg-emerald-700/20 text-sm"
                      onClick={async () => {
                        await onUpdateChannelAccess(activeChannel.id, accessSelection);
                        setAccessEditFor(null);
                        setMenuFor(null);
                      }}
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </aside>
  );
}

export default ChannelSidebar;
